import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, CheckCircle, X, FloppyDisk, ToggleLeft, ToggleRight, UserPlus, Sparkle } from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";

const emptyPlan = { name: "", price: 0, features: [], enabled: true, order: 99, description: "" };

export default function AdminPlansSection() {
  const [plans, setPlans] = useState([]);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [featureLabels, setFeatureLabels] = useState({});
  const [editing, setEditing] = useState(null); // plan object or null
  const [assignForm, setAssignForm] = useState({ user_email: "", plan_id: "" });
  const [loading, setLoading] = useState(false);

  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none";

  const load = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data.plans || []);
    setFeatureKeys(data.feature_keys || []);
    setFeatureLabels(data.feature_labels || {});
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error("Nome é obrigatório");
    setLoading(true);
    try {
      const payload = { ...editing, price: Number(editing.price), order: Number(editing.order || 99) };
      if (editing.id) {
        await api.put(`/admin/plans/${editing.id}`, payload);
        toast.success("Plano atualizado");
      } else {
        await api.post("/admin/plans", payload);
        toast.success("Plano criado");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); }
    finally { setLoading(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir plano "${p.name}"? (Usuários com esse plano ficarão sem features.)`)) return;
    await api.delete(`/admin/plans/${p.id}`);
    toast.success("Plano excluído");
    load();
  };

  const toggleEnabled = async (p) => {
    await api.put(`/admin/plans/${p.id}`, { ...p, enabled: !p.enabled });
    toast.success(!p.enabled ? "Plano habilitado" : "Plano desabilitado");
    load();
  };

  const toggleFeature = (f) => {
    const set = new Set(editing.features || []);
    if (set.has(f)) set.delete(f); else set.add(f);
    setEditing({ ...editing, features: Array.from(set) });
  };

  const assign = async () => {
    if (!assignForm.user_email) return toast.error("Informe o email do usuário");
    try {
      await api.post("/admin/plans/assign", {
        user_email: assignForm.user_email.trim(),
        plan_id: assignForm.plan_id || null,
      });
      toast.success(assignForm.plan_id ? "Plano atribuído ao usuário" : "Plano removido do usuário");
      setAssignForm({ user_email: "", plan_id: "" });
    } catch (e) { toast.error(errMsg(e, "Erro")); }
  };

  return (
    <div className="card-flat p-6 fadeUp" data-testid="admin-plans-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-caps flex items-center gap-1"><Sparkle size={12} /> Planos de Assinatura</div>
          <h3 className="font-display font-black text-2xl tracking-tighter mt-1">Gestão de Planos</h3>
          <p className="text-xs text-white/60 mt-1">Crie, edite, ative/desative planos e atribua manualmente a usuários.</p>
        </div>
        <button onClick={() => setEditing({ ...emptyPlan })} data-testid="new-plan-btn"
          className="btn-primary flex items-center gap-2 text-sm py-2">
          <Plus size={14} weight="bold" /> Novo Plano
        </button>
      </div>

      {/* LISTAGEM */}
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {plans.map((p) => (
          <div key={p.id} className={`border rounded-sm p-4 ${p.enabled ? "border-[var(--accent)]/40" : "border-white/10 opacity-70"}`}
            data-testid={`admin-plan-${p.name.toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-black text-lg">{p.name}</div>
                <div className="font-display font-black text-2xl text-[var(--accent)] tracking-tighter">
                  {brl(p.price)}<span className="text-white/40 text-xs font-normal">/mês</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => toggleEnabled(p)} title={p.enabled ? "Desabilitar" : "Habilitar"}
                  data-testid={`toggle-plan-${p.name.toLowerCase()}`}
                  className="btn-ghost !p-1">
                  {p.enabled ? <ToggleRight size={20} color="var(--success)" weight="fill" /> : <ToggleLeft size={20} className="text-white/40" />}
                </button>
              </div>
            </div>
            {p.description && <p className="text-[11px] text-white/50 mt-2">{p.description}</p>}
            <div className="mt-3 flex flex-wrap gap-1">
              {(p.features || []).map((f) => (
                <span key={f} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[var(--accent)]">
                  {featureLabels[f] || f}
                </span>
              ))}
            </div>
            <div className="flex gap-1 mt-3 pt-3 border-t border-white/5">
              <button onClick={() => setEditing({ ...p })} data-testid={`edit-plan-${p.name.toLowerCase()}`}
                className="btn-ghost text-xs flex items-center gap-1 flex-1"><PencilSimple size={12} /> Editar</button>
              <button onClick={() => remove(p)} data-testid={`del-plan-${p.name.toLowerCase()}`}
                className="btn-ghost text-xs text-[var(--danger)]"><Trash size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* ATRIBUIR PLANO MANUALMENTE */}
      <div className="border-t border-white/10 pt-4">
        <div className="label-caps flex items-center gap-1"><UserPlus size={12} /> Atribuir plano manualmente</div>
        <div className="grid md:grid-cols-[2fr_2fr_auto] gap-2 mt-3">
          <input placeholder="Email do usuário" value={assignForm.user_email}
            onChange={(e) => setAssignForm({ ...assignForm, user_email: e.target.value })}
            data-testid="assign-email" className={inp} />
          <select value={assignForm.plan_id} onChange={(e) => setAssignForm({ ...assignForm, plan_id: e.target.value })}
            data-testid="assign-plan-select" className={inp}>
            <option value="">— Remover plano —</option>
            {plans.filter((p) => p.enabled).map((p) => (
              <option key={p.id} value={p.id}>{`${p.name} · ${brl(p.price)}`}</option>
            ))}
          </select>
          <button onClick={assign} data-testid="assign-plan-btn"
            className="btn-primary text-sm px-4">
            <CheckCircle size={14} weight="bold" /> Aplicar
          </button>
        </div>
        <p className="text-[10px] text-white/40 mt-2">
          Útil para ativar plano de cortesia ou após pagamento fora do Pix. Deixe o select vazio para remover.
        </p>
      </div>

      {/* MODAL DE EDIÇÃO */}
      {editing && (
        <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-6"
            onClick={(e) => e.stopPropagation()} data-testid="plan-edit-modal">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display font-black text-xl">{editing.id ? "Editar Plano" : "Novo Plano"}</h3>
              <button onClick={() => setEditing(null)} className="btn-ghost !p-1"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-caps block mb-1">Nome</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="edit-plan-name" className={inp} placeholder="Ex: Platina" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps block mb-1">Preço (R$/mês)</label>
                  <input type="number" step="0.01" value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    data-testid="edit-plan-price" className={inp} />
                </div>
                <div>
                  <label className="label-caps block mb-1">Ordem</label>
                  <input type="number" value={editing.order}
                    onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                    data-testid="edit-plan-order" className={inp} />
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1">Descrição curta</label>
                <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className={inp} placeholder="Ex: Todos os recursos, automação total" />
              </div>
              <div>
                <label className="label-caps block mb-2">Features que este plano libera</label>
                <div className="space-y-1">
                  {featureKeys.map((f) => {
                    const active = (editing.features || []).includes(f);
                    return (
                      <button key={f} type="button" onClick={() => toggleFeature(f)}
                        data-testid={`feature-toggle-${f}`}
                        className={`w-full flex items-center justify-between border rounded-sm px-3 py-2 text-sm text-left ${active ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)]"}`}>
                        <span>{featureLabels[f] || f}</span>
                        {active ? <CheckCircle size={14} weight="fill" color="var(--accent)" /> : <div className="w-3.5 h-3.5 rounded-full border border-white/30" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                Habilitado (visível para clientes)
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={save} disabled={loading} data-testid="save-plan-btn"
                  className="btn-primary flex items-center gap-2 flex-1 justify-center">
                  <FloppyDisk size={14} /> Salvar
                </button>
                <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
